import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { usePortalWalkthrough } from './PortalWalkthroughContext';

function getSectionRoute(section, user) {
  if (section?.getRoute) return section.getRoute(user);
  return section?.route || null;
}

function routeMatchesPathname(section, pathname, user) {
  if (section?.skipNavigation) return true;
  const route = getSectionRoute(section, user);
  if (!route) return true;
  return pathname.startsWith(route.split('?')[0]);
}

export default function useNotifyTourPageReady(pageIsLoading) {
  const user = useSelector(state => state.user.zohoData);
  const { pathname } = useLocation();
  const {
    notifyPageReady,
    isActive,
    isAtCheckpoint,
    isPageReady,
    currentSection,
    pendingAutoStartPath
  } = usePortalWalkthrough() || {};

  const tourWaitsForThisPage = useMemo(
    () =>
      Boolean(
        isActive &&
          isAtCheckpoint &&
          !isPageReady &&
          currentSection &&
          routeMatchesPathname(currentSection, pathname, user)
      ),
    [isActive, isAtCheckpoint, isPageReady, currentSection, pathname, user]
  );

  const autoStartWaitsForThisPage = useMemo(() => {
    if (!pendingAutoStartPath || pageIsLoading) return false;
    const base = pathname.split('?')[0];
    return base.startsWith(pendingAutoStartPath);
  }, [pendingAutoStartPath, pathname, pageIsLoading]);

  useEffect(() => {
    if (pageIsLoading) return;
    if (!tourWaitsForThisPage && !autoStartWaitsForThisPage) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => notifyPageReady?.());
    });
  }, [
    pageIsLoading,
    tourWaitsForThisPage,
    autoStartWaitsForThisPage,
    notifyPageReady
  ]);
}
